import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { FileText, Filter, Download, Lock } from 'lucide-react';
import './ManagerApplicationsPage.css';

const ManagerApplicationsPage = () => {
    const [pin, setPin] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [pinError, setPinError] = useState('');

    // Фильтры
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Проверка пин-кода
    const handlePinSubmit = (e) => {
        e.preventDefault();
        if (pin === '3207') {
            setIsAuthenticated(true);
            setPinError('');
        } else {
            setPinError('Неверный пин-код');
        }
    };

    // Загрузка заявок
    const { data: applications, isLoading } = useQuery(
        ['managerApplications', isAuthenticated, startDate, endDate, typeFilter, formFilter, statusFilter],
        async () => {
            if (!isAuthenticated) return [];

            const params = {
                pin: '3207',
                ...(startDate && { start_date: startDate }),
                ...(endDate && { end_date: endDate }),
                ...(typeFilter && { type: typeFilter }),
                ...(formFilter && { form: formFilter }),
                ...(statusFilter && { status: statusFilter })
            };

            const response = await axios.get(`${API_BASE_URL}/applications/manager`, { params });
            return response.data;
        },
        {
            enabled: isAuthenticated,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000
        }
    );

    // Фильтрация по поисковому запросу
    const filteredApplications = useMemo(() => {
        if (!applications) return [];
        if (!searchQuery) return applications;

        const query = searchQuery.toLowerCase();
        return applications.filter(app =>
            app.group_name?.toLowerCase().includes(query) ||
            app.direction_name?.toLowerCase().includes(query) ||
            app.applicant_name?.toLowerCase().includes(query) ||
            app.applicant_email?.toLowerCase().includes(query) ||
            app.applicant_phone?.toLowerCase().includes(query) ||
            app.applicant_preferred_contact?.toLowerCase().includes(query)
        );
    }, [applications, searchQuery]);

    // Экспорт в CSV
    const handleExport = () => {
        if (!filteredApplications || filteredApplications.length === 0) return;

        const headers = [
            'Название группы',
            'Название программы',
            'Тип',
            'Форма',
            'Тренер',
            'Старт программы',
            'Дата заявки',
            'ФИО интересанта',
            'Телефон',
            'Email',
            'Способ связи',
            'Статус'
        ];

        const rows = filteredApplications.map(app => [
            app.group_name || '',
            app.direction_name || '',
            app.cycle_type || '',
            app.region_name || '',
            app.trainers?.map(t => t.name).join(', ') || '',
            app.program_start ? format(parseISO(app.program_start), 'dd.MM.yyyy') : '',
            format(parseISO(app.application_date), 'dd.MM.yyyy HH:mm'),
            app.applicant_name || '',
            app.applicant_phone || '',
            app.applicant_email || '',
            app.applicant_preferred_contact || '',
            getStatusLabel(app.status)
        ]);

        const csvContent = [
            headers.join(';'),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `заявки_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
        link.click();
    };

    const getStatusLabel = (status) => {
        const labels = {
            pending: 'Ожидает',
            approved: 'Одобрена',
            rejected: 'Отклонена',
            contacted: 'Связались'
        };
        return labels[status] || status;
    };

    const getStatusClass = (status) => {
        const classes = {
            pending: 'status-pending',
            approved: 'status-approved',
            rejected: 'status-rejected',
            contacted: 'status-contacted'
        };
        return classes[status] || '';
    };

    // Форма входа с пин-кодом
    if (!isAuthenticated) {
        return (
            <div className="manager-page">
                <div className="pin-container">
                    <div className="pin-card">
                        <div className="pin-icon">
                            <Lock size={48} />
                        </div>
                        <h1>Заявки для менеджера по обучению</h1>
                        <p>Введите пин-код для доступа к отчету</p>
                        <form onSubmit={handlePinSubmit}>
                            <input
                                type="password"
                                className="pin-input"
                                placeholder="Введите пин-код"
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                                autoFocus
                            />
                            {pinError && <div className="pin-error">{pinError}</div>}
                            <button type="submit" className="btn btn-primary">
                                Войти
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="manager-page">
            <div className="manager-header">
                <div>
                    <h1>
                        <FileText size={28} />
                        Заявки для менеджера по обучению
                    </h1>
                    <p>Онлайн и московские группы</p>
                </div>
                <button className="btn btn-primary" onClick={handleExport}>
                    <Download size={18} />
                    Экспорт в CSV
                </button>
            </div>

            <div className="filters-section">
                <div className="filters-header">
                    <Filter size={20} />
                    <h3>Фильтры</h3>
                </div>
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>Период с:</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Период по:</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Тип:</label>
                        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <option value="">Все</option>
                            <option value="1 модуль">1 модуль</option>
                            <option value="2 модуль">2 модуль</option>
                            <option value="Специализация">Специализация</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Форма:</label>
                        <select value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
                            <option value="">Все</option>
                            <option value="Онлайн">Онлайн</option>
                            <option value="Москва">Москва</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Статус:</label>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">Все</option>
                            <option value="pending">Ожидает</option>
                            <option value="contacted">Связались</option>
                            <option value="approved">Одобрена</option>
                            <option value="rejected">Отклонена</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Поиск:</label>
                        <input
                            type="text"
                            placeholder="Группа, ФИО, телефон..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                {(startDate || endDate || typeFilter || formFilter || statusFilter || searchQuery) && (
                    <button
                        className="btn btn-secondary btn-clear-filters"
                        onClick={() => {
                            setStartDate('');
                            setEndDate('');
                            setTypeFilter('');
                            setFormFilter('');
                            setStatusFilter('');
                            setSearchQuery('');
                        }}
                    >
                        Сбросить фильтры
                    </button>
                )}
            </div>

            {isLoading ? (
                <div className="loading-container">
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>Загрузка заявок...</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="stats-bar">
                        <span>Всего заявок: <strong>{filteredApplications?.length || 0}</strong></span>
                    </div>

                    <div className="table-container">
                        <table className="applications-table">
                            <thead>
                                <tr>
                                    <th>Название группы</th>
                                    <th>Программа</th>
                                    <th>Тип</th>
                                    <th>Форма</th>
                                    <th>Тренер</th>
                                    <th>Старт программы</th>
                                    <th>Дата заявки</th>
                                    <th>ФИО интересанта</th>
                                    <th>Телефон</th>
                                    <th>Email</th>
                                    <th>Способ связи</th>
                                    <th>Статус</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredApplications && filteredApplications.length > 0 ? (
                                    filteredApplications.map((app) => (
                                        <tr key={app.id}>
                                            <td className="group-name">{app.group_name}</td>
                                            <td>{app.direction_name}</td>
                                            <td>{app.cycle_type}</td>
                                            <td>{app.region_name}</td>
                                            <td className="trainers-cell">
                                                {app.trainers && app.trainers.length > 0
                                                    ? app.trainers.map(t => t.name).join(', ')
                                                    : '—'}
                                            </td>
                                            <td>
                                                {app.program_start
                                                    ? format(parseISO(app.program_start), 'dd.MM.yyyy', { locale: ru })
                                                    : '—'}
                                            </td>
                                            <td>
                                                {format(parseISO(app.application_date), 'dd.MM.yyyy HH:mm', { locale: ru })}
                                            </td>
                                            <td className="applicant-name">{app.applicant_name}</td>
                                            <td className="phone">{app.applicant_phone}</td>
                                            <td className="email">{app.applicant_email}</td>
                                            <td>{app.applicant_preferred_contact || '—'}</td>
                                            <td>
                                                <span className={`status-badge ${getStatusClass(app.status)}`}>
                                                    {getStatusLabel(app.status)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="12" className="empty-message">
                                            Заявки не найдены
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ManagerApplicationsPage;


